import type { HTMLAttributes } from 'react'
import { useId } from 'react'
import { Field, FieldDescription, FieldError, FieldLabel } from 'app/components/ui/field'
import type { AnswerValue, FormQuestion } from 'app/lib/forms/types'
import { SignaturePad } from './SignaturePad'

type Props = {
  question: FormQuestion
  value: AnswerValue | undefined
  error?: string
  onChange: (value: AnswerValue) => void
}

type InputMode = HTMLAttributes<HTMLInputElement>[`inputMode`]

/** Drives the on-screen keyboard a phone offers. Most of these patients are on a phone,
 *  so a number pad for a phone number is not a nicety. */
const TEXT_INPUT_MODE: Partial<Record<FormQuestion[`kind`], InputMode>> = {
  phone: `tel`,
  email: `email`,
  shortText: `text`,
  address: `text`,
}

/** The stored value stays lowercase because `showWhen` conditions compare against it, but the
 *  visible label is capitalised in markup rather than by CSS: `text-transform` does not change
 *  an element's accessible name, so a CSS-only capital leaves the button announced as "yes". */
const YES_NO_OPTIONS = [
  { value: `yes`, label: `Yes` },
  { value: `no`, label: `No` },
]

const INPUT_CLASS =
  `w-full rounded-lg border border-strong bg-surface px-3 py-3 text-base text-strong ` +
  `placeholder:text-minimal focus:border-[var(--form-accent)] focus:outline-none ` +
  `focus:ring-2 focus:ring-[var(--form-accent)]/30 aria-[invalid=true]:border-red-500`

const CHOICE_CLASS =
  `flex w-full items-center gap-3 rounded-lg border border-strong bg-surface px-3 py-3 ` +
  `text-left text-base text-strong hover:border-strong`

export function QuestionField({ question, value, error, onChange }: Props) {
  const inputId = useId()
  const errorId = `${inputId}-error`
  const helpId = `${inputId}-help`
  const describedBy = [error ? errorId : undefined, question.helpText ? helpId : undefined].filter(Boolean).join(` `)

  // Static text is a block of copy, often the legal wording of a consent, so it deliberately
  // does not use FieldDescription: that primitive is muted small print, which is the wrong
  // treatment for text the patient is being asked to read and agree to.
  if (question.kind === `staticText`) {
    return <p className="text-base leading-relaxed text-default">{question.label}</p>
  }

  const selected = Array.isArray(value) ? value : []
  const text = typeof value === `string` ? value : ``

  const toggleMulti = (optionId: string) => {
    onChange(selected.includes(optionId) ? selected.filter((id) => id !== optionId) : [...selected, optionId])
  }

  return (
    <Field data-invalid={Boolean(error)}>
      <FieldLabel htmlFor={inputId} className="flex items-baseline justify-between gap-3">
        <span className="text-base font-medium text-strong">{question.label}</span>
        {question.required && <span className="text-xs font-normal text-subtle">Required</span>}
      </FieldLabel>

      {question.helpText && <FieldDescription id={helpId}>{question.helpText}</FieldDescription>}

      {question.kind === `signature` || question.kind === `initials` ? (
        <SignaturePad label={question.label} value={text} onChange={onChange} />
      ) : question.kind === `longText` ? (
        <textarea
          id={inputId}
          rows={4}
          className={INPUT_CLASS}
          value={text}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy || undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : question.kind === `yesNo` ? (
        <div id={inputId} className="flex gap-3">
          {YES_NO_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              aria-pressed={text === option.value}
              className={`flex-1 rounded-lg border px-4 py-3 text-base ${
                text === option.value
                  ? `border-[var(--form-accent)] bg-[var(--form-accent)] text-[var(--form-accent-fg)]`
                  : `border-strong bg-surface text-strong`
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : question.kind === `singleSelect` ? (
        <div id={inputId} role="radiogroup" className="flex flex-col gap-2">
          {(question.options ?? []).map((option) => (
            <label key={option.id} className={CHOICE_CLASS}>
              <input type="radio" name={inputId} checked={text === option.id} onChange={() => onChange(option.id)} />
              {option.label}
            </label>
          ))}
        </div>
      ) : question.kind === `multiSelect` ? (
        <div id={inputId} className="flex flex-col gap-2">
          {(question.options ?? []).map((option) => (
            <label key={option.id} className={CHOICE_CLASS}>
              <input type="checkbox" checked={selected.includes(option.id)} onChange={() => toggleMulti(option.id)} />
              {option.label}
            </label>
          ))}
        </div>
      ) : (
        <input
          id={inputId}
          type={question.kind === `date` ? `date` : `text`}
          inputMode={TEXT_INPUT_MODE[question.kind]}
          className={INPUT_CLASS}
          value={text}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy || undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      )}

      {error && <FieldError id={errorId}>{error}</FieldError>}
    </Field>
  )
}
